import {
  Box,
  InputAdornment,
  TextField,
  TextFieldProps,
  Typography,
  useTheme,
} from '@mui/material';
import { borderColorDarkTheme, fontColorDarkTheme } from 'styles/global.styles';
import React, { ChangeEvent, ReactElement, useState } from 'react';
import IconButton from '@mui/material/IconButton';
import { Visibility, VisibilityOff } from '@mui/icons-material';

interface TextFieldComponentProps
  extends Omit<TextFieldProps, 'InputProps' | 'InputLabelProps'> {
  colorBorder?: string;
  colorText?: string;
  errorMessage?: string;
  colorLabel?: string;
  rightComponent?: ReactElement;
  iconStart?: ReactElement;
  componentEnd?: ReactElement;
  onChangeText?: (value: string) => void;
  mask?: string;
  maxLength?: number;
  isOptional?: boolean;
  label?: string;
  mb?: string | number;
  min?: string | number;
  max?: string | number;
  isVisiblePassword?: boolean;
}

const TextFieldComponent: React.FC<TextFieldComponentProps> = ({
  colorBorder,
  colorText,
  colorLabel,
  onChangeText,
  isOptional,
  label,
  rightComponent,
  errorMessage,
  iconStart,
  mask,
  maxLength,
  mb,
  min,
  max,
  isVisiblePassword = true,
  ...props
}) => {
  const [isShowPassword, setIsShowPassword] = useState(false);
  const inputLabel = isOptional && label ? `${label} (Opcional)` : label;
  const applyMask = (value: string, mask: string): string => {
    const cleanValue = value.replace(/[^a-zA-Z0-9]/g, '');
    let maskedValue = '';
    let index = 0;

    for (const char of mask) {
      if (index >= cleanValue.length) break;

      if (char === '0' && /\d/.test(cleanValue[index])) {
        maskedValue += cleanValue[index];
        index++;
      } else if (char === 'A' && /[a-zA-Z]/.test(cleanValue[index])) {
        maskedValue += cleanValue[index];
        index++;
      } else if (char === '*') {
        maskedValue += cleanValue[index];
        index++;
      } else {
        maskedValue += char;
      }
    }

    return maskedValue;
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value;

    const maskedValue = mask ? applyMask(rawValue, mask) : rawValue;

    if (onChangeText) {
      onChangeText(maskedValue);
    }
  };

  const handleClickShowPassword = () => setIsShowPassword((show) => !show);

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault();
  };

  const handleMouseUpPassword = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault();
  };

  return (
    <Box sx={{ mb: mb ?? 3, width: '100%' }}>
      <Box sx={{ display: 'flex' }}>
        <TextField
          {...props}
          label={inputLabel}
          error={errorMessage !== undefined}
          fullWidth
          type={
            props.type !== 'password'
              ? props.type
              : isShowPassword
                ? 'text'
                : 'password'
          }
          slotProps={{
            htmlInput: {
              maxLength: maxLength ?? 255,
              min: min,
              max: max,
            },
            input: {
              style: { color: colorText ?? fontColorDarkTheme },
              startAdornment: iconStart && (
                <InputAdornment position="start">{iconStart}</InputAdornment>
              ),
              endAdornment: props.type === 'password' && isVisiblePassword && (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={
                      isShowPassword
                        ? 'hide the password'
                        : 'display the password'
                    }
                    style={{ color: '#fff' }}
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                    onMouseUp={handleMouseUpPassword}
                  >
                    {isShowPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            },
            inputLabel: {
              style: {
                color: props.disabled
                  ? 'rgb(96,96,96)'
                  : (colorLabel ?? fontColorDarkTheme),
              },
            },
          }}
          onChange={handleInputChange}
          sx={{ ...props.sx }}
        />
        {rightComponent && rightComponent}
      </Box>
      {errorMessage && (
        <Typography fontSize={12} color={'error'} sx={{ mt: 1 }}>
          {errorMessage}
        </Typography>
      )}
    </Box>
  );
};

export default TextFieldComponent;
